import React, { useState, useCallback } from 'react';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import EulaPdfModal from '@components/EulaPdfModal/EulaPdfModal';
import { ErrorBox } from '../auth.styled';
import {
  Page,
  Container,
  Header,
  Title,
  Subtitle,
  PdfFrame,
  PdfFallback,
  ButtonRow,
  AcceptButton,
  DeclineButton,
} from './EulaAcceptance.styled';

const FALLBACK_EULA_URL =
  'https://aibuild-public-assets-bucket.s3.eu-west-1.amazonaws.com/eula.pdf';

const EulaAcceptance: React.FC = () => {
  const { user, acceptEula, logout, isLoading, error } = useAuth();
  const [pdfError, setPdfError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const eulaUrl = user?.eulaUrl || FALLBACK_EULA_URL;

  const handleAccept = useCallback(async () => {
    try {
      await acceptEula();
    } catch {
      // Error is set in the context
    }
  }, [acceptEula]);

  const handleDecline = useCallback(async () => {
    await logout();
  }, [logout]);

  return (
    <ThemeProvider theme={darkTheme}>
      <Page>
        <Container>
          <Header>
            <Title>End User License Agreement</Title>
            <Subtitle>
              Please read and accept the following agreement to continue using the Aibuild platform.
              If you have any questions, contact us at{' '}
              <a href="mailto:support@ai-build.com">
                support@ai-build.com
              </a>.
            </Subtitle>
          </Header>

          {!pdfError ? (
            <PdfFrame
              src={eulaUrl}
              title="End User License Agreement"
              onError={() => setPdfError(true)}
            />
          ) : (
            <PdfFallback>
              <span>Unable to load the document inline.</span>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowModal(true); }}>
                Open EULA in a popup
              </a>
            </PdfFallback>
          )}

          {error && <ErrorBox>{error}</ErrorBox>}

          <ButtonRow>
            <DeclineButton onClick={handleDecline}>
              Decline
            </DeclineButton>
            <AcceptButton
              onClick={handleAccept}
              disabled={isLoading}
              $loading={isLoading}
            >
              {isLoading ? 'Accepting...' : 'Accept'}
            </AcceptButton>
          </ButtonRow>
        </Container>
      </Page>

      {showModal && (
        <EulaPdfModal url={eulaUrl} onClose={() => setShowModal(false)} />
      )}
    </ThemeProvider>
  );
};

export default EulaAcceptance;
